import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  CONSTANTS,
  REVENU_FIELDS,
  CHARGES_FIXES_FIELDS,
  CREDITS_FIELDS,
  DEPENSES_VARIABLES_FIELDS,
  CLIENT_FIELDS,
} from "../lib/constants";
import {
  computeIndicators,
  computeDiagnostic,
  computeOptimisation,
  computeRecommendations,
  moduleCompletion,
} from "../lib/calculations";

const STORAGE_KEY = "budgetlens_state_v1";

const zeroObj = (fields) => fields.reduce((acc, f) => ({ ...acc, [f]: 0 }), {});
const emptyClient = () => CLIENT_FIELDS.reduce((acc, f) => ({ ...acc, [f]: "" }), {});

const initialState = () => ({
  client: emptyClient(),
  revenus: zeroObj(REVENU_FIELDS),
  depenses: {
    ...zeroObj(CHARGES_FIXES_FIELDS),
    ...zeroObj(CREDITS_FIELDS),
    ...zeroObj(DEPENSES_VARIABLES_FIELDS),
  },
  optimisation: {
    tauxVariables: CONSTANTS.TAUX_OPTIM_VARIABLES_DEFAUT,
    tauxFixes: CONSTANTS.TAUX_OPTIM_FIXES_DEFAUT,
  },
});

const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState();
    const parsed = JSON.parse(raw);
    // merge to be safe against schema changes
    const base = initialState();
    return {
      client: { ...base.client, ...(parsed.client || {}) },
      revenus: { ...base.revenus, ...(parsed.revenus || {}) },
      depenses: { ...base.depenses, ...(parsed.depenses || {}) },
      optimisation: { ...base.optimisation, ...(parsed.optimisation || {}) },
    };
  } catch {
    return initialState();
  }
};

const BudgetContext = createContext(null);

export const BudgetProvider = ({ children }) => {
  const [state, setState] = useState(loadFromStorage);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // quota exceeded — ignore
    }
  }, [state]);

  const setClientField = (field, value) =>
    setState((s) => ({ ...s, client: { ...s.client, [field]: value } }));

  const setRevenuField = (field, value) =>
    setState((s) => ({ ...s, revenus: { ...s.revenus, [field]: Number(value) || 0 } }));

  const setDepenseField = (field, value) =>
    setState((s) => ({ ...s, depenses: { ...s.depenses, [field]: Number(value) || 0 } }));

  const setOptimField = (field, value) =>
    setState((s) => ({ ...s, optimisation: { ...s.optimisation, [field]: Number(value) } }));

  const reset = () => setState(initialState());

  const indicators = useMemo(() => computeIndicators(state), [state]);
  const diagnostic = useMemo(() => computeDiagnostic(indicators), [indicators]);
  const optim = useMemo(
    () =>
      computeOptimisation(
        indicators,
        state.optimisation.tauxVariables,
        state.optimisation.tauxFixes
      ),
    [indicators, state.optimisation]
  );
  const recommendations = useMemo(() => computeRecommendations(indicators), [indicators]);
  const completion = useMemo(() => moduleCompletion(state), [state]);

  const value = {
    state,
    setClientField,
    setRevenuField,
    setDepenseField,
    setOptimField,
    reset,
    indicators,
    diagnostic,
    optim,
    recommendations,
    completion,
  };

  return <BudgetContext.Provider value={value}>{children}</BudgetContext.Provider>;
};

export const useBudget = () => {
  const ctx = useContext(BudgetContext);
  if (!ctx) throw new Error("useBudget must be used within BudgetProvider");
  return ctx;
};
